import React, { useState } from 'react'
import { useAuth } from '@/presentation/hooks/useAuth'
import { useTheme } from '@/presentation/hooks/useTheme'
import { webViewParamsService } from '@/core/services/WebViewParamsService'
import { appBootstrap } from '@/core/services/AppBootstrap'

export const AuthDebug: React.FC = () => {
  const [isMinimized, setIsMinimized] = useState(false)
  const { user, isAuthenticated, isLoading, refreshAuth } = useAuth()
  const { theme, actualTheme, language, isWebViewMode } = useTheme()

  const webViewParams = {
    apiToken: webViewParamsService.getApiToken(),
    language: webViewParamsService.getLanguage(),
    theme: webViewParamsService.getTheme()
  }

  const isInitialized = appBootstrap.getInitializationStatus()

  return (
    <div className={`fixed top-4 right-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg text-sm z-50 transition-all duration-200 ${
      isMinimized ? 'w-80 h-12' : 'max-w-lg max-h-[80vh] overflow-y-auto'
    }`}>
      {/* Header - different layout when minimized vs expanded */}
      <div className={`flex items-center ${
        isMinimized ? 'justify-between px-2 py-1' : 'justify-between p-3 border-b border-gray-200 dark:border-gray-700'
      }`}>
        <div className="flex items-center space-x-2">
          <h3 className={`font-bold text-gray-800 dark:text-gray-200 ${
            isMinimized ? 'text-sm' : 'text-lg'
          }`}>
            🔄 Auth Debug
          </h3>
          {/* Show status info inline when minimized */}
          {isMinimized && (
            <>
              <span className={`w-2 h-2 rounded-full ${isAuthenticated ? 'bg-green-500' : 'bg-red-500'}`}></span>
              <span className="text-gray-600 dark:text-gray-400 text-xs">
                Auth: {isAuthenticated ? '✅' : '❌'}
              </span>
              <span className="text-gray-600 dark:text-gray-400 text-xs">
                Theme: {actualTheme}
              </span>
            </>
          )}
        </div>
        <button
          onClick={() => setIsMinimized(!isMinimized)}
          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
          title={isMinimized ? 'Expand debug panel' : 'Minimize debug panel'}
        >
          {isMinimized ? '⬆️' : '⬇️'}
        </button>
      </div>

      {/* Content - only show when not minimized */}
      {!isMinimized && (
        <div className="p-4">
          <div className="space-y-4">
            {/* App Bootstrap Status */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">AppBootstrap Status:</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Initialized:</span>
                  <span className={`font-semibold ${isInitialized ? 'text-green-600' : 'text-orange-600'}`}>
                    {isInitialized ? '✅ Yes' : '⏳ Loading...'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">WebView Token:</span>
                  <span className={`font-mono text-xs ${webViewParams.apiToken ? 'text-green-600' : 'text-gray-400'}`}>
                    {webViewParams.apiToken || 'None'}
                  </span>
                </div>
              </div>
            </div>

            {/* AuthProvider Status */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">AuthProvider Status:</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Loading:</span>
                  <span className={`font-semibold ${isLoading ? 'text-orange-600' : 'text-green-600'}`}>
                    {isLoading ? '⏳ Yes' : '✅ No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Authenticated:</span>
                  <span className={`font-semibold ${isAuthenticated ? 'text-green-600' : 'text-red-600'}`}>
                    {isAuthenticated ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">User ID:</span>
                  <span className={`font-mono text-xs ${user ? 'text-green-600' : 'text-gray-400'}`}>
                    {user?.id || 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">User Type:</span>
                  <span className={`font-mono text-xs ${user ? 'text-blue-600' : 'text-gray-400'}`}>
                    {user?.type || 'None'}
                  </span>
                </div>
              </div>
            </div>

            {/* ThemeProvider Status */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-1">ThemeProvider Status:</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Theme Setting:</span>
                  <span className="font-mono text-xs text-purple-600">{theme}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Actual Theme:</span>
                  <span className="font-mono text-xs text-purple-600">{actualTheme}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Language:</span>
                  <span className="font-mono text-xs text-blue-600">{language}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">WebView Mode:</span>
                  <span className={`font-semibold ${isWebViewMode ? 'text-green-600' : 'text-gray-600'}`}>
                    {isWebViewMode ? '✅ Yes' : '❌ No'}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Actions:</h4>
              <button
                onClick={refreshAuth}
                disabled={isLoading}
                className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white text-sm rounded transition-colors"
              >
                {isLoading ? '⏳ Refreshing...' : '🔄 Refresh Auth'}
              </button>
            </div>

            {/* Architecture Info */}
            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
              <h5 className="font-semibold text-blue-800 dark:text-blue-300 text-xs mb-1">
                ✨ Refactored Architecture
              </h5>
              <div className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                <p>• <strong>AppBootstrap:</strong> Initialization + WebView token validation</p>
                <p>• <strong>AuthProvider:</strong> Reactive auth state management</p>
                <p>• <strong>ThemeProvider:</strong> Dedicated theme/language management</p>
                <p>• <strong>Clean separation:</strong> No duplicate responsibilities</p>
              </div>
            </div>

            {/* Test URLs */}
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              <p className="font-semibold mb-1">💡 Test URLs:</p>
              <p>• ?k=test-driver-123&lang=pt&theme=dark</p>
              <p>• ?k=valid-admin-456&lang=en&theme=light</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}